import React, { useState } from "react";
import SyncFusionDateTimePicker from "../Pages/Components/SyncFusionDateTimePicker/SyncFusionDateTimePicker";

const FUEL_TYPES_FALLBACK = [
  { fuelTypeId: 1, fuelTypeDescription: "Gasolina" },
  { fuelTypeId: 2, fuelTypeDescription: "Etanol" },
  { fuelTypeId: 3, fuelTypeDescription: "Diesel" },
  { fuelTypeId: 4, fuelTypeDescription: "GNV" },
  { fuelTypeId: 5, fuelTypeDescription: "Elétrico" },
];

const makeEmptyForm = () => ({
  fuelTypeId: "",
  quantity: "",
  amount: "",
  dateTime: new Date(),
});

const toLocalISOString = (date) => {
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.000`
  );
};

const getFuelId = (ft) =>
  ft.FuelTypeId ?? ft.fuelTypeId ?? ft.id ?? "";

const getFuelLabel = (ft) =>
  ft.FuelTypeDescription ?? ft.fuelTypeDescription ??
  ft.name ?? ft.fuelTypeName ?? ft.description ?? "—";

const FuelForm = ({ fuelTypes = [], vehicleId, isSaving = false, onSave }) => {
  const [form, setForm]         = useState(makeEmptyForm);
  const [errors, setErrors]     = useState({});
  const [dateKey, setDateKey]   = useState(0);

  const resolvedFuelTypes =
    fuelTypes.length > 0 ? fuelTypes : FUEL_TYPES_FALLBACK;

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!vehicleId)       errs.vehicleId  = "Selecione um veículo antes de salvar.";
    if (!form.dateTime)   errs.dateTime   = "Obrigatório";
    if (!form.fuelTypeId) errs.fuelTypeId = "Obrigatório";
    if (!form.quantity || isNaN(Number(form.quantity)) || Number(form.quantity) <= 0)
      errs.quantity = "Informe um valor positivo";
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      errs.amount = "Informe um valor positivo";
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    const payload = {
      vehicleId:     Number(vehicleId),
      fuelTypeId:    Number(form.fuelTypeId),
      quantity:      Number(form.quantity),
      amount:        Number(form.amount),
      refuelingDate: toLocalISOString(form.dateTime),
    };

    const result = await onSave(payload);
    if (result !== null) handleClear();
  };

  const handleClear = () => { setForm(makeEmptyForm()); setErrors({}); setDateKey((k) => k + 1); };

  return (
    <div className="card mb-3">
      <div className="card-body">

        {!vehicleId && (
          <p className="text-muted small mb-3">
            Selecione um veículo para registrar abastecimentos.
          </p>
        )}

        {errors.vehicleId && (
          <div className="alert alert-warning py-2 mb-3">
            {errors.vehicleId}
          </div>
        )}

        <div className="row g-3">

          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label text3-medium mb-1">
              Data/Hora Abastecimento <span className="text-danger">*</span>
            </label>
            <SyncFusionDateTimePicker
              key={`fuelDateTime-${dateKey}`}
              id="fuelDateTime"
              value={form.dateTime}
              onChange={(val) => {
                setForm((p) => ({ ...p, dateTime: val }));
                setErrors((p) => ({ ...p, dateTime: null }));
              }}
              placeholder="dd/mm/aaaa hh:mm"
              step={15}
              width="100%"
            />
            {errors.dateTime && (
              <small className="text-danger d-block mt-1">{errors.dateTime}</small>
            )}
          </div>

          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label text3-medium mb-1">
              Combustível <span className="text-danger">*</span>
            </label>
            <select
              className={`form-control${errors.fuelTypeId ? " is-invalid" : ""}`}
              value={form.fuelTypeId}
              onChange={set("fuelTypeId")}
            >
              <option value="">-- Selecione --</option>
              {resolvedFuelTypes.map((ft, i) => (
                <option key={`fuel-${i}-${getFuelId(ft)}`} value={getFuelId(ft)}>
                  {getFuelLabel(ft)}
                </option>
              ))}
            </select>
            {errors.fuelTypeId && (
              <small className="text-danger d-block mt-1">{errors.fuelTypeId}</small>
            )}
          </div>

          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label text3-medium mb-1">
              Quantidade (l/m³) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={`form-control${errors.quantity ? " is-invalid" : ""}`}
              placeholder="Ex: 35,93"
              value={form.quantity}
              onChange={set("quantity")}
            />
            {errors.quantity && (
              <small className="text-danger d-block mt-1">{errors.quantity}</small>
            )}
          </div>

          <div className="col-12 col-sm-6 col-md-3">
            <label className="form-label text3-medium mb-1">
              Valor (R$) <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              className={`form-control${errors.amount ? " is-invalid" : ""}`}
              placeholder="Ex: 218,84"
              value={form.amount}
              onChange={set("amount")}
            />
            {errors.amount && (
              <small className="text-danger d-block mt-1">{errors.amount}</small>
            )}
          </div>

        </div>

        <div className="actions-buttons mt-3">
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? "Salvando..." : "Salvar"}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClear}
            disabled={isSaving}
          >
            Limpar
          </button>
        </div>

      </div>
    </div>
  );
};

export default FuelForm;
